import { ConstraintType } from '../types/enums.js';
// ============================================================================
// Constraint Descriptions
// ============================================================================
/**
 * Human-readable descriptions for each constraint type.
 */
export const CONSTRAINT_DESCRIPTIONS = {
    [ConstraintType.ASAP]: {
        name: 'As Soon As Possible',
        abbreviation: 'ASAP',
        description: 'Schedules the task to start as early as possible based on dependencies and project start date.',
        example: 'Task will start immediately after its predecessors finish.',
    },
    [ConstraintType.ALAP]: {
        name: 'As Late As Possible',
        abbreviation: 'ALAP',
        description: 'Schedules the task to start as late as possible without delaying dependent tasks or the project.',
        example: 'Task will start at the latest date that still allows successors to finish on time.',
    },
    [ConstraintType.MSO]: {
        name: 'Must Start On',
        abbreviation: 'MSO',
        description: 'Forces the task to start on the specified constraint date, regardless of dependencies.',
        example: 'Task must begin exactly on January 15th.',
    },
    [ConstraintType.MFO]: {
        name: 'Must Finish On',
        abbreviation: 'MFO',
        description: 'Forces the task to finish on the specified constraint date, regardless of dependencies.',
        example: 'Task must complete exactly on January 31st.',
    },
    [ConstraintType.SNET]: {
        name: 'Start No Earlier Than',
        abbreviation: 'SNET',
        description: 'Ensures the task does not start before the specified constraint date.',
        example: 'Task cannot start before January 10th, but may start later if needed.',
    },
    [ConstraintType.SNLT]: {
        name: 'Start No Later Than',
        abbreviation: 'SNLT',
        description: 'Ensures the task starts on or before the specified constraint date.',
        example: 'Task must start by January 20th at the latest.',
    },
    [ConstraintType.FNET]: {
        name: 'Finish No Earlier Than',
        abbreviation: 'FNET',
        description: 'Ensures the task does not finish before the specified constraint date.',
        example: 'Task cannot complete before February 1st.',
    },
    [ConstraintType.FNLT]: {
        name: 'Finish No Later Than',
        abbreviation: 'FNLT',
        description: 'Ensures the task finishes on or before the specified constraint date.',
        example: 'Task must complete by February 15th at the latest.',
    },
};
/**
 * Categorizes constraints by their flexibility level.
 *
 * - Flexible: Task dates can move freely based on dependencies
 * - Semi-flexible: Task dates have some restrictions but can move within bounds
 * - Inflexible: Task dates are locked to specific dates
 */
export const CONSTRAINT_FLEXIBILITY = {
    [ConstraintType.ASAP]: 'flexible',
    [ConstraintType.ALAP]: 'flexible',
    [ConstraintType.SNET]: 'semi_flexible',
    [ConstraintType.SNLT]: 'semi_flexible',
    [ConstraintType.FNET]: 'semi_flexible',
    [ConstraintType.FNLT]: 'semi_flexible',
    [ConstraintType.MSO]: 'inflexible',
    [ConstraintType.MFO]: 'inflexible',
};
/**
 * Get all constraints of a specific flexibility level.
 */
export function getConstraintsByFlexibility(flexibility) {
    return Object.entries(CONSTRAINT_FLEXIBILITY)
        .filter(([, f]) => f === flexibility)
        .map(([constraint]) => constraint);
}
/**
 * Flexible constraints - dates move freely.
 */
export const FLEXIBLE_CONSTRAINTS = getConstraintsByFlexibility('flexible');
/**
 * Semi-flexible constraints - dates have bounds.
 */
export const SEMI_FLEXIBLE_CONSTRAINTS = getConstraintsByFlexibility('semi_flexible');
/**
 * Inflexible constraints - dates are locked.
 */
export const INFLEXIBLE_CONSTRAINTS = getConstraintsByFlexibility('inflexible');
export const CONSTRAINT_BEHAVIORS = {
    [ConstraintType.ASAP]: {
        requiresDate: false,
        affectsStart: true,
        affectsFinish: false,
        canCauseConflict: false,
        honorInForwardPass: true,
        honorInBackwardPass: false,
    },
    [ConstraintType.ALAP]: {
        requiresDate: false,
        affectsStart: true,
        affectsFinish: false,
        canCauseConflict: false,
        honorInForwardPass: false,
        honorInBackwardPass: true,
    },
    [ConstraintType.MSO]: {
        requiresDate: true,
        affectsStart: true,
        affectsFinish: false,
        canCauseConflict: true,
        honorInForwardPass: true,
        honorInBackwardPass: true,
    },
    [ConstraintType.MFO]: {
        requiresDate: true,
        affectsStart: false,
        affectsFinish: true,
        canCauseConflict: true,
        honorInForwardPass: true,
        honorInBackwardPass: true,
    },
    [ConstraintType.SNET]: {
        requiresDate: true,
        affectsStart: true,
        affectsFinish: false,
        canCauseConflict: false,
        honorInForwardPass: true,
        honorInBackwardPass: false,
    },
    [ConstraintType.SNLT]: {
        requiresDate: true,
        affectsStart: true,
        affectsFinish: false,
        canCauseConflict: true,
        honorInForwardPass: false,
        honorInBackwardPass: true,
    },
    [ConstraintType.FNET]: {
        requiresDate: true,
        affectsStart: false,
        affectsFinish: true,
        canCauseConflict: false,
        honorInForwardPass: true,
        honorInBackwardPass: false,
    },
    [ConstraintType.FNLT]: {
        requiresDate: true,
        affectsStart: false,
        affectsFinish: true,
        canCauseConflict: true,
        honorInForwardPass: false,
        honorInBackwardPass: true,
    },
};
// ============================================================================
// Constraint Validation
// ============================================================================
/**
 * Validates if a constraint type requires a constraint date.
 */
export function constraintRequiresDate(constraintType) {
    return CONSTRAINT_BEHAVIORS[constraintType].requiresDate;
}
/**
 * Checks if a constraint can potentially cause scheduling conflicts.
 */
export function constraintCanCauseConflict(constraintType) {
    return CONSTRAINT_BEHAVIORS[constraintType].canCauseConflict;
}
/**
 * Gets the scheduling direction(s) where a constraint is honored.
 */
export function getConstraintSchedulingDirection(constraintType) {
    const behavior = CONSTRAINT_BEHAVIORS[constraintType];
    if (behavior.honorInForwardPass && behavior.honorInBackwardPass) {
        return 'both';
    }
    if (behavior.honorInForwardPass) {
        return 'forward';
    }
    if (behavior.honorInBackwardPass) {
        return 'backward';
    }
    return 'none';
}
// ============================================================================
// Default Constraint for New Tasks
// ============================================================================
export const DEFAULT_CONSTRAINT_TYPE = ConstraintType.ASAP;
//# sourceMappingURL=constraints.js.map